import { Component, Host, h, Prop, Element, Fragment, Event, EventEmitter } from "@stencil/core";

import {
  StzhMenuItemFocusEvent,
  StzhMenuItemBlurEvent,
  StzhMenuItemClickEvent,
  StzhBadgeType,
  StzhMenuSize,
  StzhMenuVariant,
} from "../../index";

import { hasSlot } from "../../utils/utils";

/**
 * @slot - Slot for any content
 * @slot icon - Slot for `stzh-icon` element
 */
@Component({
  tag: "stzh-menu-item",
  styleUrl: "stzh-menu-item.scss",
  scoped: true,
})
export class StzhMenuItem {
  /** Variant */
  @Prop({ reflect: true }) variant: StzhMenuVariant = "default";

  /** Size */
  @Prop({ reflect: true }) size: StzhMenuSize = "default";

  /** Counter */
  @Prop() counter: number;

  /** Badge (only displayed if `icon` property or icon slot is used) */
  @Prop() badge: string = "";

  /** Badge type */
  @Prop({ reflect: true }) badgeType: StzhBadgeType = "default";

  /** Whether badge should be displayed empty */
  @Prop({ reflect: true }) badgeEmpty: boolean = false;

  /** Whether the button is disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether the button is active */
  @Prop({ reflect: true }) active: boolean = false;

  /** Icon (use instead of icon slot) */
  @Prop() icon: string = "";

  /** Label */
  @Prop() label: string = "";

  /** `href` if the button should be used as link  */
  @Prop() href: string = "";

  /** Target if the button is used as link (if `href` used)  */
  @Prop() target: string = "";

  /** Accessible label for screen readers to replace visible text */
  @Prop({ attribute: "a11y-label" }) a11yLabel: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link/button element.
   * Default value will be taken from `label` prop or default slot.
   */
  @Prop() analyticsId: string;

  @Element() element: HTMLStzhMenuItemElement;

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhMenuItemFocusEvent>;

  /** Blur event */
  @Event() stzhBlur: EventEmitter<StzhMenuItemBlurEvent>;

  /** Click event */
  @Event() stzhClick: EventEmitter<StzhMenuItemClickEvent>;

  private menuItem: HTMLButtonElement | HTMLAnchorElement;
  private text: HTMLDivElement;

  private focusedByInput: boolean = false;

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.menuItem.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-menu-item",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-menu-item",
      originalEvent: event,
    });
  };

  private onClick = (event: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-menu-item",
      originalEvent: event,
      href: this.href,
    });
  };

  componentDidRender() {
    requestAnimationFrame(() => {
      this.menuItem?.setAttribute("s-object-id", this.analyticsId || this.text.innerText);
    });
  }

  private renderInner(iconUsed: boolean): DocumentFragment {
    return (
      <Fragment>
        {iconUsed && (
          <div class="stzh-menu-item__icon-wrapper">
            {this.icon ? (
              <stzh-icon class="stzh-menu-item__icon" name={this.icon}></stzh-icon>
            ) : (
              <slot name="icon"></slot>
            )}
            {(this.badge || this.badgeEmpty) && (
              <stzh-badge class="stzh-menu-item__badge" label={this.badge} type={this.badgeType}></stzh-badge>
            )}
          </div>
        )}
        <div class="stzh-menu-item__text-wrapper">
          <div ref={el => (this.text = el as HTMLDivElement)} class="stzh-menu-item__text">
            {this.label ? this.label : <slot></slot>}
          </div>
          {this.counter !== undefined && this.counter > 0 && (
            <div class="stzh-menu-item__counter">({this.counter})</div>
          )}
        </div>
      </Fragment>
    );
  }

  render() {
    const iconUsed: boolean = hasSlot(this.element, "icon") || !!this.icon;
    const classes = {
      "stzh-menu-item": true,
      "stzh-menu-item--has-icon": iconUsed,
      "stzh-menu-item--has-badge": !!this.badge,
      "stzh-menu-item--is-disabled": this.disabled,
      "stzh-menu-item--is-active": this.active,
      [`stzh-menu-item--size-${this.size}`]: !!this.size,
      [`stzh-menu-item--${this.variant}`]: !!this.variant,
    };

    return (
      <Host role="menuitem" tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        {this.href ? (
          <a
            ref={el => (this.menuItem = el as HTMLAnchorElement)}
            href={this.disabled ? null : this.href}
            target={this.target}
            class={classes}
            aria-label={this.a11yLabel || null}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
          >
            {this.renderInner(iconUsed)}
          </a>
        ) : (
          <button
            ref={el => (this.menuItem = el as HTMLButtonElement)}
            class={classes}
            type="button"
            disabled={this.disabled}
            aria-label={this.a11yLabel || null}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
          >
            {this.renderInner(iconUsed)}
          </button>
        )}
      </Host>
    );
  }
}
